
import {
	Container, Title, Card, Text, Space, Pagination, Group, Center,
	Badge, createStyles, rem, Stack, Affix, Transition, Button,
	Loader
} from '@mantine/core'
import Footer from '../components/Footer';
import Head from '../components/Head';
import ReactMarkdown from 'react-markdown';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { IconArrowUp, IconHistory } from '@tabler/icons-react'

// ── Changelog data ───────────────────────────────────────────────────────────
const changelogPosts = [

	{
		id: 1,
		version: "v7.4.3",
		title: "Welcome to the new Changelog!",
		date: "June 5, 2026",
		summary: "First public release of Comedy Seen ticketing system.",
		imageUrl: "https://www.getcomedyseen.com/social-share-card.png",
	},
	{
		id: 2,
		version: "v7.5.0",
		title: null,
		date: "June 11, 2026",
		summary: null,
		imageUrl: null
	},
	{
		id: 3,
		version: "v7.6.0",
		title: null,
		date: "June 21, 2026",
		summary: null,
		imageUrl: null
	}

];

const POSTS_PER_PAGE = 5;

// ── Styles ───────────────────────────────────────────────────────────────────
const useStyles = createStyles((theme) => ({

	heroWrapper: {
		background: 'radial-gradient(ellipse at 50% 60%, #f0e6ff 0%, #e8f6fb 50%, #f4fbff 100%)',
		paddingTop: rem(32),
		paddingBottom: rem(36),
		textAlign: 'center',
	},

	changelogTitle: {
		fontFamily: "'Merriweather', 'Georgia', serif",
		fontWeight: 900,
		letterSpacing: '-0.01em',
		fontSize: rem(60),
		color: '#5c0099',

		[theme.fn.smallerThan('md')]: { fontSize: rem(44) },
		[theme.fn.smallerThan('sm')]: { fontSize: rem(34) },
	},

	postCard: {
		borderLeft: `4px solid #7b00b8`,
		transition: 'box-shadow 0.2s ease',
		'&:hover': { boxShadow: theme.shadows.md },
	},

	postTitle: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(28),
		lineHeight: 1.2,

		[theme.fn.smallerThan('sm')]: { fontSize: rem(22) },
	},
}))

// ── Main component ────────────────────────────────────────────────────────────
function Changelog() {
	const { classes } = useStyles()

	const links = [
		{ "link": "/About", "label": "About" },
		{ "link": "/Support", "label": "Support" },
		{ "link": "/Pp", "label": "Privacy" },
		{ "link": "/Tos", "label": "Terms of Use" },
		{ "link": "/Changelog", "label": "Changelog" }
	]

	const [searchParams, setSearchParams] = useSearchParams()
	const pageParam = searchParams.get('page')
	const currentPage = pageParam ? parseInt(pageParam) : 1

	const orderedPosts = useMemo(() => [...changelogPosts].reverse(), []);

	// Calculate pagination
	const totalPages = Math.ceil(orderedPosts.length / POSTS_PER_PAGE)
	const currentPagePosts = useMemo(() => {
		const startIndex = (currentPage - 1) * POSTS_PER_PAGE
		return orderedPosts.slice(startIndex, startIndex + POSTS_PER_PAGE)
	}, [currentPage, orderedPosts])

	const currentUrl = window.location.href

	// ── New Content State Handling ───────────────────────────────────────────
	const [postsContent, setPostsContent] = useState<Record<number, string>>({})
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [hasError, setHasError] = useState<boolean>(false)

	useEffect(() => {
		if (currentPagePosts.length === 0) return

		setIsLoading(true)
		setHasError(false)

		// Fetch all markdown files for the current page posts in parallel
		const fetchPromises = currentPagePosts.map(post =>
			fetch(`/changelog/${post.id}.md`)
				.then((response) => {
					if (!response.ok) {
						throw new Error(`Markdown file for post ${post.id} not found`)
					}
					return response.text()
				})
				.then((text) => ({ id: post.id, text }))
		)

		Promise.all(fetchPromises)
			.then((results) => {
				const contentMap: Record<number, string> = {}
				results.forEach(res => {
					contentMap[res.id] = res.text
				})
				setPostsContent(contentMap)
				setIsLoading(false)
			})
			.catch((err) => {
				console.error('Error fetching changelog content:', err)
				setHasError(true)
				setIsLoading(false)
			})
	}, [currentPagePosts])

	// Back-to-top visibility
	const [showScrollTop, setShowScrollTop] = useState(false)
	const handleScroll = useCallback(() => {
		setShowScrollTop(window.scrollY > 400)
	}, [])
	useEffect(() => {
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [handleScroll])

	const handlePageChange = (newPage: number) => {
		setSearchParams({ page: newPage.toString() })
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	return (
		<div className="changelog-wrapper">

			<Helmet>
				<title>Changelog | Comedy Seen Updates</title>
				<meta name="description" content="Keep track of the latest updates, features, and improvements to Comedy Seen." />
				<meta property="og:type" content="website" />
				<meta property="og:title" content="Comedy Seen Changelog" />
				<meta property="og:description" content="Keep track of the latest updates, features, and improvements to Comedy Seen." />
				<meta property="og:image" content="https://www.getcomedyseen.com/social-share-card.png" />
				<meta property="og:url" content={currentUrl} />
			</Helmet>

			<Head />

			{/* ── Hero ── */}
			<div className={classes.heroWrapper}>
				<Group position="center" spacing="xs" mb={6}>
					<IconHistory size={32} color="#7b00b8" />
				</Group>
				<Title className={classes.changelogTitle}>Changelog</Title>
				<Text size="md" color="dimmed" mt={6} italic>What's new in Comedy Seen</Text>
			</div>

			<Container size="md" py="xl">

				{isLoading && (
					<Center py="xl" style={{ minHeight: '300px' }}>
						<Loader color="violet" size="md" variant="dots" />
					</Center>
				)}

				{hasError && (
					<Center py="xl" style={{ minHeight: '300px' }}>
						<Text color="red" size="sm" weight={500}>
							Bummer! Couldn't load the update log. Let's blame it on the tech and try refreshing.
						</Text>
					</Center>
				)}

				{!isLoading && !hasError && (
					<Stack spacing={40}>
						{currentPagePosts.map((post) => (
							<Card
								key={post.id}
								className={classes.postCard}
								shadow="sm"
								padding="xl"
								radius="md"
								withBorder
							>
								{/* Version & Date badges */}
								<Group mb="sm" spacing="xs">
									<Badge
										color="violet"
										variant="filled"
										size="md"
										radius="sm"
										sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}
									>
										{post.version}
									</Badge>
									<Badge
										color="gray"
										variant="light"
										size="md"
										radius="sm"
										sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
									>
										{post.date}
									</Badge>
								</Group>

								{/* Post title */}
								<Title order={2} className={classes.postTitle} mb="lg">
									{post.title}
								</Title>

								{/* Markdown content */}
								<div className="changelog-prose">
									<ReactMarkdown>
										{postsContent[post.id] || ''}
									</ReactMarkdown>
								</div>
							</Card>
						))}
					</Stack>
				)}

				<Space h="xl" />

				{/* ── Centered pagination ── */}
				{totalPages > 1 && (
					<Center mt="xl">
						<Pagination
							total={totalPages}
							value={currentPage}
							onChange={handlePageChange}
							size="lg"
							color="violet"
						/>
					</Center>
				)}

				<Space h="xl" />
			</Container>

			<Footer links={links} />

			{/* ── Back to top ── */}
			<Affix position={{ bottom: rem(24), right: rem(24) }}>
				<Transition transition="slide-up" mounted={showScrollTop}>
					{(transitionStyles) => (
						<Button
							style={transitionStyles}
							color="violet"
							variant="filled"
							radius="xl"
							size="sm"
							leftIcon={<IconArrowUp size={16} />}
							onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
							sx={{
								boxShadow: '0 4px 16px rgba(91,0,153,0.3)',
								transition: 'transform 0.2s ease, box-shadow 0.2s ease',
								'&:hover': {
									transform: 'translateY(-2px)',
									boxShadow: '0 8px 24px rgba(91,0,153,0.4)',
								},
							}}
						>
							Top
						</Button>
					)}
				</Transition>
			</Affix>

		</div>
	)
}

export default Changelog
